import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Sparkle, FloppyDisk, Eye, Trash, SpinnerGap, Plus, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const BLANK = {
  title: '', subtitle: '', excerpt: '', body: '',
  author_name: 'Derwin', hero_image_url: '',
  tags: '', category: '', related_experiment: '', published: false,
};

const withAuth = () => ({ withCredentials: true });

export const BlogAdminListPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await axios.get(`${API_URL}/api/blog/admin/posts`, withAuth()); setPosts(r.data.posts); }
    catch (e) { toast.error(e.response?.status === 403 ? 'Admin only' : 'Could not load'); navigate('/'); }
    finally { setLoading(false); }
  };
  const togglePub = async (p) => {
    await axios.put(`${API_URL}/api/blog/admin/posts/${p.slug}`, { published: !p.published }, withAuth());
    toast.success(p.published ? 'Unpublished' : 'Published');
    load();
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await axios.delete(`${API_URL}/api/blog/admin/posts/${p.slug}`, withAuth());
    toast.success('Deleted'); load();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-admin-page">
      <SEO title="Blog Admin — TurfPulse" description="" />
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground"><ArrowLeft size={14} /> Back</button>
          <span className="text-sm font-bold">Blog Admin</span>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/blog/calendar')} data-testid="blog-admin-calendar-btn">Calendar</Button>
            <Button size="sm" onClick={() => navigate('/admin/blog/new')} data-testid="blog-admin-new-btn"><Plus size={14} className="mr-1" /> New</Button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <h1 className="text-3xl font-bold">Posts</h1>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && posts.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground">No posts. Click New to generate a draft.</CardContent></Card>}
        <div className="space-y-2" data-testid="blog-admin-list">
          {posts.map(p => (
            <Card key={p.slug} data-testid={`blog-admin-row-${p.slug}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${p.published ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/20 text-amber-700 dark:text-amber-400'}`}>{p.published ? 'Live' : 'Draft'}</span>
                    {p.category && <span className="text-[10px] text-muted-foreground">{p.category}</span>}
                    <span className="text-[10px] text-muted-foreground">{p.read_time_minutes}m</span>
                  </div>
                  <div className="font-semibold truncate">{p.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.excerpt}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/blog/${p.slug}`)} title="Preview"><Eye size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/blog/${p.slug}/edit`)}>Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => togglePub(p)}>{p.published ? 'Unpub' : 'Publish'}</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(p)} title="Delete"><Trash size={14} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export const BlogAdminEditorPage = () => {
  const navigate = useNavigate();
  const { slug: editSlug } = useParams();
  const isEdit = !!editSlug;
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [genOpen, setGenOpen] = useState(!isEdit);
  const [gen, setGen] = useState({ topic: '', target_keyword: '', angle: '', related_experiment: '', word_count: 1800 });
  const [generating, setGenerating] = useState(false);

  // Autosave state — protects drafts from accidental tab close / browser crash.
  const [dirty, setDirty] = useState(false);
  const [autosaving, setAutosaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [autosaveError, setAutosaveError] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const currentSlugRef = useRef(editSlug || null);
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { currentSlugRef.current = editSlug || currentSlugRef.current; }, [editSlug]);

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API_URL}/api/blog/admin/posts/${editSlug}`, withAuth())
      .then(r => { const d = r.data; setForm({ ...d, tags: (d.tags || []).join(', ') }); setLastSavedAt(Date.now()); })
      .catch(() => toast.error('Load failed'))
      .finally(() => setLoading(false));
  }, [editSlug, isEdit]);

  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const buildPayload = (f) => ({
    ...f,
    tags: typeof f.tags === 'string' ? f.tags.split(',').map(t => t.trim()).filter(Boolean) : f.tags,
  });

  const save = async () => {
    if (!form.title || !form.excerpt || !form.body) { toast.error('Title + excerpt + body required'); return; }
    setSaving(true);
    const payload = buildPayload(form);
    try {
      if (currentSlugRef.current) {
        await axios.put(`${API_URL}/api/blog/admin/posts/${currentSlugRef.current}`, payload, withAuth());
        toast.success('Saved');
      } else {
        const r = await axios.post(`${API_URL}/api/blog/admin/posts`, payload, withAuth());
        currentSlugRef.current = r.data.slug;
        toast.success('Created');
        navigate(`/admin/blog/${r.data.slug}/edit`);
      }
      setDirty(false); setLastSavedAt(Date.now()); setAutosaveError(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Autosave — 10 sec after last change, only if fields minimally filled.
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(async () => {
      const f = formRef.current;
      if (!f.title || !f.body) return; // need at least title+body to persist
      setAutosaving(true);
      try {
        const payload = buildPayload(f);
        if (currentSlugRef.current) {
          await axios.put(`${API_URL}/api/blog/admin/posts/${currentSlugRef.current}`, payload, withAuth());
        } else {
          const r = await axios.post(`${API_URL}/api/blog/admin/posts`, { ...payload, excerpt: payload.excerpt || payload.title.slice(0, 180) }, withAuth());
          currentSlugRef.current = r.data.slug;
          // Silently replace URL so refreshes keep the draft. Avoid re-fetch by not navigating away.
          window.history.replaceState(null, '', `/admin/blog/${r.data.slug}/edit`);
        }
        setDirty(false); setLastSavedAt(Date.now()); setAutosaveError(null);
      } catch (e) {
        setAutosaveError(e.response?.data?.detail || 'Autosave failed');
      } finally { setAutosaving(false); }
    }, 10000);
    return () => clearTimeout(t);
  }, [dirty, form]);

  // Tick clock so "saved Xs ago" counts up.
  useEffect(() => {
    const i = setInterval(() => setNowTick(Date.now()), 5000);
    return () => clearInterval(i);
  }, []);

  // Warn on close with unsaved changes.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const savedAgo = () => {
    if (!lastSavedAt) return 'Not saved yet';
    const s = Math.max(0, Math.round((nowTick - lastSavedAt) / 1000));
    if (s < 5) return 'Saved just now';
    if (s < 60) return `Saved ${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `Saved ${m}m ago`;
    return `Saved ${Math.round(m / 60)}h ago`;
  };

  const generate = async () => {
    if (!gen.topic || gen.topic.length < 10) { toast.error('Topic must be at least 10 chars'); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API_URL}/api/blog/admin/generate`, gen, withAuth());
      const d = r.data.draft;
      setForm(f => ({
        ...f,
        title: d.title, subtitle: d.subtitle || '', excerpt: d.excerpt, body: d.body,
        tags: (d.tags || []).join(', '), category: d.category || '',
        related_experiment: d.related_experiment || '',
      }));
      setDirty(true);
      setGenOpen(false);
      toast.success('Draft generated — edit freely');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><SpinnerGap size={24} className="animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="blog-editor-page">
      <SEO title={isEdit ? 'Edit Post' : 'New Post'} description="" />
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 px-4 py-3">
          <button onClick={() => navigate('/admin/blog')} className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0"><ArrowLeft size={14} /> List</button>
          <div className="flex items-center gap-2 text-xs min-w-0 flex-1 justify-center">
            {autosaving ? (
              <span className="flex items-center gap-1 text-muted-foreground" data-testid="blog-editor-autosave-status"><SpinnerGap size={12} className="animate-spin" /> Saving…</span>
            ) : autosaveError ? (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400" data-testid="blog-editor-autosave-status" title={autosaveError}><WarningCircle size={12} weight="fill" /> Autosave paused</span>
            ) : dirty ? (
              <span className="text-muted-foreground" data-testid="blog-editor-autosave-status">Unsaved…</span>
            ) : (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400" data-testid="blog-editor-autosave-status"><CheckCircle size={12} weight="fill" /> {savedAgo()}</span>
            )}
          </div>
          <Button size="sm" onClick={save} disabled={saving} data-testid="blog-editor-save">{saving ? <SpinnerGap size={14} className="animate-spin" /> : <><FloppyDisk size={14} className="mr-1" /> Save</>}</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* LLM generator */}
        <Card className="border-primary/30 bg-primary/5" data-testid="blog-editor-generator">
          <CardHeader className="pb-2 flex-row items-center justify-between cursor-pointer" onClick={() => setGenOpen(v => !v)}>
            <CardTitle className="text-base flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-primary" /> AI Draft Generator</CardTitle>
            <span className="text-xs text-muted-foreground">{genOpen ? 'Hide' : 'Show'}</span>
          </CardHeader>
          {genOpen && (
            <CardContent className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Topic (what's the post about?)</Label>
                <Input value={gen.topic} onChange={e => setGen({ ...gen, topic: e.target.value })} placeholder="e.g. Why the 37-minute sprinkler zone runtime is wrong" data-testid="gen-topic" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Target keyword</Label>
                  <Input value={gen.target_keyword} onChange={e => setGen({ ...gen, target_keyword: e.target.value })} data-testid="gen-keyword" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Word count</Label>
                  <Input type="number" value={gen.word_count} onChange={e => setGen({ ...gen, word_count: parseInt(e.target.value) || 1800 })} data-testid="gen-wordcount" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Angle / extra context</Label>
                <Textarea rows={2} value={gen.angle} onChange={e => setGen({ ...gen, angle: e.target.value })} placeholder="Specific perspective, real experience, data you want included..." data-testid="gen-angle" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Related experiment (optional)</Label>
                <Input value={gen.related_experiment} onChange={e => setGen({ ...gen, related_experiment: e.target.value })} placeholder="EXP-001" data-testid="gen-exp" />
              </div>
              <Button onClick={generate} disabled={generating} data-testid="gen-submit">{generating ? <><SpinnerGap size={14} className="animate-spin mr-1" /> Generating (~20 sec)…</> : <><Sparkle size={14} weight="fill" className="mr-1" /> Generate draft</>}</Button>
              <p className="text-[10px] text-muted-foreground">Uses Gemini 2.5 Flash via Emergent LLM Key. Overwrites any unsaved fields below.</p>
            </CardContent>
          )}
        </Card>

        {/* Editor */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={e => setField('title', e.target.value)} data-testid="blog-editor-title" />
            </div>
            <div>
              <Label>Subtitle (optional)</Label>
              <Input value={form.subtitle || ''} onChange={e => setField('subtitle', e.target.value)} data-testid="blog-editor-subtitle" />
            </div>
            <div>
              <Label>Excerpt (~180 chars, shows in cards + meta)</Label>
              <Textarea rows={2} value={form.excerpt} onChange={e => setField('excerpt', e.target.value)} data-testid="blog-editor-excerpt" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Category</Label><Input value={form.category || ''} onChange={e => setField('category', e.target.value)} data-testid="blog-editor-category" /></div>
              <div><Label>Tags (comma-sep)</Label><Input value={form.tags} onChange={e => setField('tags', e.target.value)} data-testid="blog-editor-tags" /></div>
            </div>
            <div><Label>Hero image URL (optional)</Label><Input value={form.hero_image_url || ''} onChange={e => setField('hero_image_url', e.target.value)} data-testid="blog-editor-hero" /></div>
            <div><Label>Related experiment (optional, e.g. EXP-001)</Label><Input value={form.related_experiment || ''} onChange={e => setField('related_experiment', e.target.value)} data-testid="blog-editor-exp" /></div>
            <div>
              <Label>Body (markdown)</Label>
              <Textarea rows={30} value={form.body} onChange={e => setField('body', e.target.value)} className="font-mono text-sm" data-testid="blog-editor-body" />
              <p className="text-[10px] text-muted-foreground mt-1">Supports GitHub-flavored markdown: tables, checklists, code fences, strikethrough.</p>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-border/40">
              <input type="checkbox" id="pub-check" checked={form.published} onChange={e => setField('published', e.target.checked)} data-testid="blog-editor-publish-check" />
              <label htmlFor="pub-check" className="text-sm font-medium">Published (live on blog)</label>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
